import _ from 'lodash'
import { getSpecificAttrValue, replaceSpecialChars, divideBy100, checkIsYoungFund } from 'utils'
import { getCustomIntl, getGlobalParams } from 'config/config'

const tcLanguageTag = 'zh-TW'
const enLanguageTag = 'en-001'

const intl = getCustomIntl()
const { countryConfig } = getGlobalParams()

const rule = {
  fundId: '${fundId}',
  isin: '${shareclassFacets.isin}',
  taCode: '${shareclassFacets.taCode}',
  shareClassLaunchDate: '${shareclassFacets.shareClassLaunchDate}',
  isYoungFund: (data) => {
    return countryConfig.specialTreatmentISINsOfYoungFund.includes(data.shareclassFacets.isin)
      ? false
      : checkIsYoungFund(data.shareclassFacets.shareClassLaunchDate)
  },
  fundName: (data) => {
    const fundNameZh = getSpecificAttrValue({ originData: data.fundName, attrValue: tcLanguageTag, valuePlaceholder: '' })
    const fundNameEn = getSpecificAttrValue({ originData: data.fundName, attrValue: enLanguageTag, valuePlaceholder: '' })

    return {
      en: fundNameEn || fundNameZh,
      zh: fundNameZh || fundNameEn
    }
  },
  fundOriginalName: (data) => getSpecificAttrValue({ originData: data.fundOriginalName, attrValue: tcLanguageTag, valuePlaceholder: '' }),
  shareClassId: '${shareclassFacets.scId}',
  shareClassName: (data) => {
    const shareClassNameArray = data.shareclassFacets.shareclassName
    const shareClassNameZh = getSpecificAttrValue({ originData: shareClassNameArray, attrValue: tcLanguageTag })
    const shareClassNameEn = getSpecificAttrValue({ originData: shareClassNameArray, attrValue: enLanguageTag })
    const fomrattedShareClassName = replaceSpecialChars(shareClassNameEn)

    return {
      en: shareClassNameEn,
      zh: shareClassNameZh,
      fomrattedShareClassName
    }
  },
  shareClassNote: (data) =>
    getSpecificAttrValue({ originData: data.shareclassFacets.shareclassNote, attrValue: tcLanguageTag, valuePlaceholder: '' }),
  documents: (data) => {
    if (data.types) {
      const documentsTypes = Array.isArray(data.types) ? data.types : [data.types]
      return documentsTypes.map((document) => ({
        title: getSpecificAttrValue({ originData: document.data, attrValue: tcLanguageTag, targetAttr: 'documentName' }),
        href: getSpecificAttrValue({ originData: document.data, attrValue: tcLanguageTag, targetAttr: 'url' })
      }))
    }

    return []
  },
  price: {
    nav: (data) => parseFloat(data.price.nav.nav),
    changeAbsolute: (data) => parseFloat(data.price.changeAbsolute),
    changePercent: (data) => (data.price && data.price.changePercent ? divideBy100(data.price.changePercent) : '-'),
    date: '${price.nav.priceDate}'
  },
  currency: (data) => {
    const { currencyCode, data: currencyArray } = data.shareclassFacets.baseCurrency
    const currencyName =
      getSpecificAttrValue({ originData: currencyArray, attrValue: tcLanguageTag }) || intl.formatMessage({ id: 'fundList.map.other' })

    return {
      currencyCode,
      name: currencyName
    }
  },
  performance: (data) => {
    const isYoungFund = countryConfig.specialTreatmentISINsOfYoungFund.includes(data.shareclassFacets.isin)
      ? false
      : checkIsYoungFund(data.shareclassFacets.shareClassLaunchDate)

    if (isYoungFund) {
      return null
    }

    const formattedPerformance = _(data.performance)
      .toPairs()
      .map(([key, value]) => {
        if (!_.isNull(value) && !Number.isNaN(_.toNumber(value))) {
          return [key, divideBy100(_.toNumber(value))]
        }

        return [key, value]
      })
      .fromPairs()
      .value()

    return formattedPerformance
  },
  msRating: (data) => {
    const { msRating } = data
    const rating = msRating ? msRating.rating : ''
    const ratingName = `${rating}${intl.formatMessage({ id: rating ? 'fundList.map.riskRating' : 'nonRiskRating' })}`
    return {
      rating,
      ratingName
    }
  },
  fundRangeCode: (data) => {
    const isOnShore = data?.fundRange?.rangeCode === 'TW-ONSHORE'
    const longFundRangeCodeName = intl.formatMessage({ id: isOnShore ? 'fundList.map.onShore' : 'fundList.map.offShore' })

    const shortFundRangeCodeName = intl.formatMessage({ id: isOnShore ? 'fundList.map.onShoreShort' : 'fundList.map.offShoreShort' })
    const fundRangeCodeValue = isOnShore ? 'TW-ONSHORE' : 'TW-OFFSHORE'

    return {
      value: fundRangeCodeValue,
      longName: longFundRangeCodeName,
      shortName: shortFundRangeCodeName
    }
  },
  shareClassType: (data) => {
    const shareClassType = getSpecificAttrValue({
      originData: data.shareclassFacets.classType.data,
      attrValue: tcLanguageTag,
      valuePlaceholder: ''
    })

    return shareClassType || intl.formatMessage({ id: 'fundList.map.other' })
  },
  investmentRegion: (data) => {
    const investmentRegion = getSpecificAttrValue({
      originData: data.investmentRegion.data,
      attrValue: tcLanguageTag,
      valuePlaceholder: ''
    })

    return investmentRegion || intl.formatMessage({ id: 'fundList.map.other' })
  },
  dividendType: (data) => {
    const dividendType = getSpecificAttrValue({
      originData: data.shareclassFacets.dividendType.data,
      attrValue: tcLanguageTag,
      valuePlaceholder: ''
    })

    return dividendType || intl.formatMessage({ id: 'fundList.map.other' })
  },
  fundRating: (data) => data.fundRating || intl.formatMessage({ id: 'fundList.map.other' }),
  assetClass: (data) => {
    const { assetClassId, data: assetClassData } = data.assetClass
    const assetClassName =
      getSpecificAttrValue({ originData: assetClassData, attrValue: tcLanguageTag }) || intl.formatMessage({ id: 'fundList.map.other' })
    return {
      assetClassId,
      name: assetClassName
    }
  }
}

export default rule
